import { useState } from "react";
import { AtSymbolIcon } from "@heroicons/react/solid";

import { forgetPassword } from "../../utils/api";
import { ReactComponent as ForgotPasswordImage } from "../../assets/images/forgot.svg";

import Button from "../SharedComponents/Button";
import Container from "./Container";
import Input from "./Input";
import Alert, { ALERT_TYPE } from "./Alert";
import Notification from "../SharedComponents/Notification";

function ForgotPassword() {
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [resetPasswordLinkSent, setResetPasswordLinkSent] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const notifications = [
        {
            id: "1",
            type: "info",
            active: true,
            title: "Set password link has been sent",
            message: "A link to set your password has been e-mailed to " + email,
            primaryActionText: "Go to login",
            primaryActionLink: "/login",
            secondaryButton: false,
            icon: false,
            closeButton: false,
        },
    ];

    const handleSendPasswordResetLink = async () => {
        if (!email) {
            setAlert("Invalid email address");
            setAlertType(ALERT_TYPE.ERROR);
            return;
        }
        setLoading(true);
        try {
            await forgetPassword(email);
            setResetPasswordLinkSent(true);
            setAlertType(ALERT_TYPE.SUCCESS);
        } catch (err) {
            setAlert(err.message);
            setResetPasswordLinkSent(false);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLoading(false);
    };

    const handleFormChange = (value) => {
        setEmail(value);
    };

    const handleKeyPress = (key) => {
        if (key === "Enter") handleSendPasswordResetLink();
    };

    return (
        <Container>
            <div className="lg:w-1/2 md:full p-8 grid grid-flow-row gap-6">
                <div className="grid grid-flow-row gap-6">
                    <div className="grid-flow-row grid gap-4">
                        <div className="flex items-start justify-between">
                            <h1 className="font-semibold tracking-tight text-3xl text-indigo-900 text-left">
                                We will send you a link to set your password
                            </h1>
                        </div>
                        {!resetPasswordLinkSent && (
                            <p className="text-left text-gray-500">
                                Enter your email address below to set your password
                            </p>
                        )}
                    </div>
                    {resetPasswordLinkSent ? (
                        <div className="flex flex-col gap-2">
                            {notifications.map((notification) => (
                                <Notification
                                    key={notification.id}
                                    active={notification.active}
                                    type={notification.type}
                                    title={notification.title}
                                    message={notification.message}
                                    primaryActionText={notification.primaryActionText}
                                    primaryActionLink={notification.primaryActionLink}
                                    secondaryButton={notification.secondaryButton}
                                    icon={notification.icon}
                                    closeButton={notification.closeButton}
                                />
                            ))}
                            <div
                                className={
                                    "grid grid-flow-row gap-2 w-full mx-auto px-3 py-3 " +
                                    "rounded-md bg-amber-100 text-amber-700"
                                }
                            >
                                <p className="font-medium">Didn't receive the set password link?</p>
                                <p className="text-sm text-left">
                                    <span className="">
                                        Refresh the page and try again later
                                    </span>
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                            <div className="w-full">
                                <Input
                                    id="username"
                                    type="text"
                                    name="Email"
                                    placeholder="Enter email address"
                                    Icon={<AtSymbolIcon className="h-4 w-4 text-gray-500" />}
                                    value={email}
                                    onChange={handleFormChange}
                                    onKeyPress={handleKeyPress}
                                />
                            </div>
                            <div className="grid grid-flow-row gap-2">
                                <Button
                                    text="Send set password link"
                                    onClick={handleSendPasswordResetLink}
                                    extraStyling={
                                        "w-full px-3 py-3 font-medium rounded-md focus:outline-none " +
                                        "button hover:bg-primary-700 text-base text-white focus:bg-primary-700 " +
                                        "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                        "focus:outline-none focus:ring-primary-600 justify-center h-12"
                                    }
                                    loading={loading}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="hidden lg:block w-1/2 py-10 px-10 bg-gradient-to-r from-primary-50 to-secondary-50">
                <ForgotPasswordImage className="h-56 w-full sm:h-72 md:h-96 lg:h-72" />
            </div>
        </Container>
    );
}

export default ForgotPassword;
